import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _jsxFileName = "/home/christian/DEV/workspace/crossbuilders/form-library/src/lib/components/fields/NumberInput/index.jsx";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import Label from '../../structure/Label';
import FormHint from '../../structure/FormHint';
import FormRow from '../../structure/FormRow';
import { inputPropTypes, inputDefaultProps } from '../../../commonProps';
import { isNumber, transformToNumberFormat } from '../../../services/numberConverterService';
import useDebounce from '../../../hooks/useDebounce';
function NumberInputComponent(props) {
  var id = props.id,
    name = props.name,
    inputRef = props.inputRef,
    placeholder = props.placeholder,
    unitLabel = props.unitLabel,
    onChange = props.onChange,
    onBlur = props.onBlur,
    value = props.value,
    children = props.children,
    labelClass = props.labelClass,
    inputClass = props.inputClass,
    errorClass = props.errorClass,
    rowClass = props.rowClass,
    format = props.format,
    mask = props.mask,
    returnFormat = props.returnFormat,
    errors = props.errors,
    disabled = props.disabled,
    readOnly = props.readOnly,
    min = props.min,
    max = props.max,
    step = props.step,
    minErrorMessage = props.minErrorMessage,
    maxErrorMessage = props.maxErrorMessage,
    delay = props.delay,
    warning = props.warning,
    info = props.info,
    decimalScale = props.decimalScale,
    fixedDecimalScale = props.fixedDecimalScale,
    type = props.type,
    onValueChange = props.onValueChange,
    allowNegative = props.allowNegative,
    removeFormatting = props.removeFormatting,
    isAllowed = props.isAllowed,
    prefix = props.prefix,
    suffix = props.suffix,
    allowedDecimalSeparators = props.allowedDecimalSeparators;
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    inputValue = _useState2[0],
    setInputValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    minReached = _useState4[0],
    setMinReached = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    maxReached = _useState6[0],
    setMaxReached = _useState6[1];
  var debouncedInputValue = useDebounce(inputValue, delay);
  var getValueMinMax = useCallback(function (val) {
    setMinReached(false);
    setMaxReached(false);
    if (isNumber(min) && val < min) {
      setMinReached(true);
      return min;
    }
    if (isNumber(max) && val > max) {
      setMaxReached(true);
      return max;
    }
    return val;
  }, [min, max]);
  var parsedValue = useMemo(function () {
    if (!value) return null;
    return parseFloat(String(value).replace(/\./g, '').replace(/,/g, '.'));
  }, [value]);
  var steppedValue = useMemo(function () {
    if (!isNumber(step) || !isNumber(parsedValue)) return null;
    var remaining = parsedValue % step;
    if (remaining >= step / 2) {
      return getValueMinMax(parsedValue + (step - remaining));
    }
    return getValueMinMax(parsedValue - remaining);
  }, [step, parsedValue, getValueMinMax]);
  var handleChange = useCallback(function (event) {
    onValueChange(event, function (_ref) {
      var floatValue = _ref.floatValue,
        formattedValue = _ref.formattedValue;
      setInputValue(returnFormat === 'formatted' ? formattedValue : floatValue);
    });
  }, [onValueChange, returnFormat]);
  var handleBlur = useCallback(function (event) {
    var prepareValueForChange = function prepareValueForChange(val) {
      return {
        floatValue: val,
        formattedValue: transformToNumberFormat({
          number: val
        })
      };
    };
    if (isNumber(parsedValue)) {
      var valueMinMax = getValueMinMax(parsedValue);
      if (parsedValue !== valueMinMax) {
        handleChange(prepareValueForChange(valueMinMax));
      }
      if (isNumber(step) && steppedValue) {
        handleChange(prepareValueForChange(steppedValue));
      }
    }
    onBlur(event);
  }, [step, parsedValue, onBlur, getValueMinMax, steppedValue, handleChange]);
  var errorMessage = useMemo(function () {
    if (errors && Object.hasOwnProperty.call(errors, name)) {
      return errors[name].message;
    }
    if (minErrorMessage && minReached) {
      return minErrorMessage;
    }
    if (maxErrorMessage && maxReached) {
      return maxErrorMessage;
    }
    return null;
  }, [errors, name, minReached, maxReached, minErrorMessage, maxErrorMessage]);
  var inputClassName = errorMessage ? "".concat(inputClass, " ").concat(errorClass) : inputClass;
  useEffect(function () {
    onChange(debouncedInputValue);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [debouncedInputValue]);
  useEffect(function () {
    return setInputValue(value);
  }, [value]);
  return /*#__PURE__*/React.createElement(FormRow, {
    className: rowClass,
    hasErrors: !!errorMessage,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 146,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(Label, {
    id: id,
    className: labelClass,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 147,
      columnNumber: 13
    }
  }, children), /*#__PURE__*/React.createElement("div", {
    className: "form-element-unit-wrapper",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 149,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(NumberFormat, {
    onValueChange: handleChange,
    onBlur: handleBlur,
    getInputRef: inputRef,
    value: inputValue,
    id: id,
    name: name,
    thousandSeparator: ".",
    decimalSeparator: ",",
    decimalScale: decimalScale,
    fixedDecimalScale: fixedDecimalScale,
    className: inputClassName,
    allowNegative: allowNegative,
    placeholder: placeholder,
    type: type,
    mask: mask,
    format: format,
    disabled: disabled,
    readOnly: readOnly,
    allowedDecimalSeparators: allowedDecimalSeparators,
    removeFormatting: removeFormatting,
    isAllowed: isAllowed,
    prefix: prefix,
    suffix: suffix,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 150,
      columnNumber: 17
    }
  }), unitLabel ? /*#__PURE__*/React.createElement("span", {
    className: "form-element__unit",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 176,
      columnNumber: 30
    }
  }, unitLabel) : null), /*#__PURE__*/React.createElement(FormHint, {
    error: errorMessage,
    warning: warning,
    info: info,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 179,
      columnNumber: 13
    }
  }));
}
export default function NumberInput(props) {
  var _this = this;
  return /*#__PURE__*/React.createElement(Controller, Object.assign({
    defaultValue: null
  }, props, {
    render: function render(_ref2) {
      var _onChange = _ref2.onChange,
        _onBlur = _ref2.onBlur,
        value = _ref2.value;
      return /*#__PURE__*/React.createElement(NumberInputComponent, Object.assign({}, props, {
        onChange: function onChange(payload) {
          _onChange(payload);
          props.onChange(payload);
        },
        onBlur: function onBlur(payload) {
          _onBlur(payload);
          props.onBlur(payload);
        },
        value: value,
        __self: _this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 191,
          columnNumber: 13
        }
      }));
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 190,
      columnNumber: 9
    }
  }));
}
NumberInput.propTypes = _objectSpread(_objectSpread({}, inputPropTypes), {}, {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unitLabel: PropTypes.string,
  format: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  mask: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  returnFormat: PropTypes.oneOf(['value', 'float', 'formatted']),
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  onMinReached: PropTypes.func,
  onMaxReached: PropTypes.func,
  delay: PropTypes.number,
  decimalScale: PropTypes.number,
  fixedDecimalScale: PropTypes.bool,
  type: PropTypes.oneOf(['text', 'tel', 'password']),
  onValueChange: PropTypes.func,
  allowNegative: PropTypes.bool,
  allowedDecimalSeparators: PropTypes.array,
  removeFormatting: PropTypes.func,
  isAllowed: PropTypes.func,
  prefix: PropTypes.string,
  suffix: PropTypes.string
});
NumberInput.defaultProps = _objectSpread(_objectSpread({}, inputDefaultProps), {}, {
  value: null,
  unitLabel: null,
  format: null,
  mask: '',
  returnFormat: 'float',
  min: null,
  max: null,
  step: null,
  onMinReached: null,
  onMaxReached: null,
  delay: 500,
  decimalScale: undefined,
  fixedDecimalScale: false,
  type: 'text',
  onValueChange: function onValueChange(event, callback) {
    callback(event);
  },
  allowNegative: false,
  allowedDecimalSeparators: [','],
  removeFormatting: undefined,
  isAllowed: undefined,
  prefix: undefined,
  suffix: undefined
});